import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';
import { ActivatedRoute, Router } from '@angular/router';
import { defer, forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, distinctUntilChanged, finalize, flatMap, takeUntil, tap } from 'rxjs/operators';
import { ConfirmDialogComponent } from '../../shared/confirm-dialog/confirm-dialog.component';
import { HttpErrorResponse } from '@angular/common/http';
import { AfregningDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/afregning-dto';
import { GetPriceServiceaftaleResponseDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/get-price-serviceaftale-response-dto';
import { ServiceaftaleDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/serviceaftale-dto';
import { TilvalgGroupDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/tilvalg-group-dto';
import { ServiceAftaleService, TilvalgService, AfregningService, ServiceAbonnementService, PriceService } from 'src/app/api/services';
import { EGIS_SERVICE_CALLER } from 'src/app/constants';
import { ErrorHandlerService } from 'src/app/services/error-handler.service';
import { LoaderService } from 'src/app/services/loader.service';
import { UpdateServiceabonnementRequest } from 'src/app/api/models/OK/Egis/Service/Models/Request/update-serviceabonnement-request';
import { abonnementValidator } from 'src/app/validators/abonnement.validator';
import { ServiceAbonnementDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/service-abonnement-dto';
import { MatRadioChange } from '@angular/material/radio';
import { GetPriceRequest } from 'src/app/api/models/OK/Egis/Service/Models/Request/get-price-request';
import { LogicService } from 'src/app/services/logic.service';
import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { ServiceintervalDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/serviceinterval-dto';
import { OpkraevningsDialogData, OpkraevningsDialogResponse } from '../../aftale/models';
import { OpkraevningsIntervalDialogComponent } from '../../aftale/create/forms/abonnement-form/opkraevnings-interval-dialog/opkraevnings-interval-dialog.component';
import { fadeSlideInOut, fadeSlideInOutResume, inOutAnimation } from 'src/app/common/animations';
import { ChangeEffect } from 'src/app/api/models/OK/Egis/Service/Models/Request/change-effect';

@Component({
  selector: 'app-edit-abonnement',
  templateUrl: './edit-abonnement.component.html',
  styleUrls: ['./edit-abonnement.component.scss'],
  animations: [fadeSlideInOut, fadeSlideInOutResume, inOutAnimation],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class EditAbonnementComponent implements OnInit {
  @ViewChild('stepper', { static: false }) public stepper: MatStepper;

  quickViewOpen: boolean = false;
  onStageResume: boolean = false;
  stepperIndex: number;

  serviceaftaleNo: number;
  choice: number;

  abonnementForm: UntypedFormGroup;
  tilvalgForm: UntypedFormGroup;
  opkraevningForm: UntypedFormGroup;
  periodeForm: UntypedFormGroup;
  datoForm: UntypedFormGroup;
  sendLetterForm: UntypedFormGroup;

  today: Date = new Date();
  minPaymentDate = new Date();
  minCheckupDate = new Date();

  serviceaftale: ServiceaftaleDto;
  afregninger: AfregningDto[] = [];
  selectedAbonnement: ServiceAbonnementDto;
  abonnementer: ServiceAbonnementDto[];
  tilvalgGroups: TilvalgGroupDto[];
  selectedTilvalgGroups: TilvalgGroupDto[];

  currentPrice: GetPriceServiceaftaleResponseDto;
  newPrice: GetPriceServiceaftaleResponseDto;
  priceForKorrektion: GetPriceServiceaftaleResponseDto;

  sidsteOpkraevning: Date | null;
  currentServiceinterval: ServiceintervalDto | null;
  nytServiceinterval: ServiceintervalDto | null;
  tilpassetServiceinterval: ServiceintervalDto | null;
  selectedServiceinterval: ServiceintervalDto | null;

  validFrom: Date;
  upcomingChange: boolean = false;

  opkraevningerTotalMedMoms: number;
  opkraevningerTotalExclMoms: number;

  afregningerTotalMedMoms: number;
  afregningerTotalExclMoms: number;

  data$: Observable<any>;

  afregninger$: Observable<any>;
  unsubscribe$ = new Subject<void>();

  isTilpasningMulig: boolean = true;

  priceError: string;

  isGarantiNaerVarmeLeasingGasTryghed: boolean = false;

  get abonnement() {
    return this.abonnementForm.get('abonnement');
  }

  get tilvalg() {
    return this.tilvalgForm.get('tilvalgGroups');
  }

  get opkraevningType() {
    return this.opkraevningForm.get('opkraevningType');
  }

  get naesteEftersynDate() {
    return this.datoForm.get('naesteEftersynDate');
  }

  get opkraevningDate() {
    return this.datoForm.get('opkraevningDate');
  }

  get opkraevningsinterval() {
    return this.datoForm.get('opkraevningsinterval');
  }

  get eftersynInterval() {
    return this.datoForm.get('eftersynInterval');
  }

  get tilpas() {
    return this.periodeForm.get('tilpas');
  }

  get sendLetter() {
    return this.sendLetterForm.get('sendLetter');
  }

  get effectiveOpkraevningsinterval() {
    return this.opkraevningsinterval.value ?? this.selectedAbonnement.opkraevningInterval;
  }

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private serviceaftaleService: ServiceAftaleService,
    private priceService: PriceService,
    private tilvalgService: TilvalgService,
    private loader: LoaderService,
    private errorHandler: ErrorHandlerService,
    private formBuilder: UntypedFormBuilder,
    private dialog: MatDialog,
    private afregningService: AfregningService,
    private serviceabonnementService: ServiceAbonnementService,
    private logic: LogicService,
  ) {
    this.validFrom = new Date(this.route.snapshot.queryParams.validFrom);
    this.upcomingChange = this.validFrom > new Date();

    this.abonnementForm = this.formBuilder.group(
      {
        abonnement: new UntypedFormControl(null, Validators.required),
      },
      {
        validators: abonnementValidator,
      },
    );

    this.datoForm = this.formBuilder.group(
      {
        naesteEftersynDate: [this.logic.calculateDefaultCheckupDate(new Date())],
        opkraevningDate: [new Date()],
        opkraevningsinterval: [null],
        eftersynInterval: [],
      },
      {
        validators: abonnementValidator,
      },
    );

    this.tilvalgForm = this.formBuilder.group({
      tilvalgGroups: this.formBuilder.array([]),
    });
    this.opkraevningForm = this.formBuilder.group({
      opkraevningType: [null, [Validators.required]],
    });
    this.periodeForm = this.formBuilder.group({
      tilpas: [this.upcomingChange ? false : true, [Validators.required]],
    });

    this.sendLetterForm = this.formBuilder.group({
      sendLetter: [true, Validators.required],
    });
  }

  ngOnInit(): void {
    this.serviceaftaleNo = Number(this.route.snapshot.paramMap.get('serviceaftaleNo'));

    this.data$ = defer(() => {
      this.loader.show();
      return this.serviceaftaleService.getServiceaftaleByNo({ caller: EGIS_SERVICE_CALLER, serviceaftaleNo: this.serviceaftaleNo }).pipe(
        finalize(() => {
          this.loader.hide();
        }),
        takeUntil(this.unsubscribe$),
        catchError((err) => {
          this.errorHandler.HandleError(err);
          return of(null);
        }),
        tap((serviceaftale: ServiceaftaleDto) => {
          this.serviceaftale = serviceaftale;

          this.isGarantiNaerVarmeLeasingGasTryghed =
            serviceaftale.kunde?.kundeExtendedInformation?.harNaervarmeAftale ||
            serviceaftale.kunde?.kundeExtendedInformation?.harLeasingAftale ||
            serviceaftale.kunde?.kundeExtendedInformation?.harGasfyrTryghedAftale;

          this.sendLetter.setValue(!this.isGarantiNaerVarmeLeasingGasTryghed);

          this.eftersynInterval.setValue(serviceaftale.serviceAbonnement.eftersynInterval);

          let eftersynDate = serviceaftale.naesteEftersynDate ? serviceaftale.naesteEftersynDate : serviceaftale.baseEftersynDate;
          this.naesteEftersynDate.setValue(new Date(eftersynDate ? eftersynDate : new Date()));

          let opkraevningsDate = serviceaftale.opkraevningDate ? serviceaftale.opkraevningDate : serviceaftale.baseOpkraevningDate;
          this.opkraevningDate.setValue(new Date(opkraevningsDate ? opkraevningsDate : new Date()));

          this.opkraevningsinterval.setValue(serviceaftale.opkraevningsinterval ? serviceaftale.opkraevningsinterval : null);
          this.minPaymentDate = this.getMinPaymentDate(serviceaftale);
          this.minCheckupDate = this.getMinCheckupDate();

          this.currentServiceinterval = serviceaftale.currentServiceinterval;
          if (serviceaftale.currentServiceinterval) {
            this.nytServiceinterval = this.calculateNytServiceinterval(serviceaftale.serviceAbonnement, this.validFrom);
            this.tilpassetServiceinterval = this.calculateTilpassetServiceinterval(
              serviceaftale.serviceAbonnement,
              serviceaftale.currentServiceinterval,
            );
          }

          this.abonnement.setValue(serviceaftale.serviceAbonnement.id);
          this.getCurrentPrice();
          this.abonnementForm.updateValueAndValidity();
          this.loader.show();
        }),
        flatMap((serviceaftale: ServiceaftaleDto) =>
          forkJoin([
            this.afregningService
              .getCustomerOpkraevningerForCurrentServiceinterval({
                serviceaftaleId: serviceaftale.id,
                caller: EGIS_SERVICE_CALLER,
              })
              .pipe(
                tap((response) => {
                  this.afregninger = response.filter((p) => !p.hasKreditering && !p.kreditnotaForServiceafregningId);
                  this.afregningerTotalMedMoms = this.afregninger.reduce((sum, current) => sum + current.beloebMedMoms, 0);
                  this.afregningerTotalExclMoms = this.afregninger.reduce((sum, current) => sum + current.beloebExclMoms, 0);
                }),
              ),
            this.tilvalgService
              .getTilvalgGroupsForServiceabonnement({
                serviceAbonnementId: serviceaftale.serviceAbonnement.id,
                caller: EGIS_SERVICE_CALLER,
              })
              .pipe(
                tap((getTilvalgGroupsResponse: TilvalgGroupDto[]) => {
                  this.tilvalgGroups = getTilvalgGroupsResponse;
                  const control = <UntypedFormArray>this.tilvalgForm.controls['tilvalgGroups'];
                  control.clear();
                  getTilvalgGroupsResponse.forEach((x) => {
                    control.push(new UntypedFormControl(this.findValueForControl(x), [Validators.required]));
                  });

                  this.tilvalgForm.valueChanges
                    .pipe(takeUntil(this.unsubscribe$), distinctUntilChanged())
                    .subscribe((tilvalgInformation) => {
                      if (tilvalgInformation.tilvalgGroups.length > 0) {
                        this.getNewPrice();
                      }
                    });
                }),
              ),
            this.serviceabonnementService
              .getAvailableAbonnements({
                caller: EGIS_SERVICE_CALLER,
                produktId: serviceaftale.serviceAbonnement.product.id,
                kundeTypeId: serviceaftale.kunde.kundetype.id,
              })
              .pipe(
                tap((response) => {
                  this.abonnementer = response;
                }),
              ),
          ]).pipe(
            finalize(() => {
              this.loader.hide();
            }),
          ),
        ),
      );
    });
  }

  private findValueForControl(tilvalgGroup: TilvalgGroupDto) {
    let value = null;
    this.serviceaftale.tilvalg.forEach((x) => {
      tilvalgGroup.tilvalg.forEach((y) => {
        if (x.tilvalgnr == y.tilvalgnr) {
          value = x.tilvalgnr;
        }
      });
    });
    return value;
  }

  onCloseClick() {
    if (!this.abonnementForm.dirty) {
      this.router.navigate(['/aftale', this.serviceaftale.okKundeAftaleNr], { fragment: 'Abonnement' });
    } else {
      let dialogRef = this.dialog.open(ConfirmDialogComponent, {
        data: {
          title: 'Er du sikker?',
          body: 'Er du sikker på at du vil afbryde?',
        },
      });

      dialogRef.afterClosed().subscribe((confirm) => {
        if (confirm) {
          this.router.navigate(['/aftale', this.serviceaftale.okKundeAftaleNr], { fragment: 'Abonnement' });
        }
      });
    }
  }

  doChange(stepper: MatStepper) {
    let tilvalgnrList = !this.selectedAbonnement.isTilkaldsabonnement
      ? this.tilvalg.value.filter((x) => x != null && Number.isSafeInteger(x))
      : null;

    const body: UpdateServiceabonnementRequest = {
      serviceaftaleId: this.serviceaftale.id,
      serviceabonnementId: this.abonnement.value,
      tilvalgNrList: tilvalgnrList,
      naesteEftersynDate: this.selectedAbonnement.isTilkaldsabonnement ? null : new Date(this.naesteEftersynDate.value).toOkDateString(),
      opkraevningDate: this.selectedAbonnement.isTilkaldsabonnement ? null : new Date(this.opkraevningDate.value).toOkDateString(),
      opkraevningsinterval: this.selectedAbonnement.isTilkaldsabonnement ? null : this.opkraevningsinterval.value,
      validFrom: this.validFrom.toOkDateString(),
      krediter: this.upcomingChange ? null : this.opkraevningType.value,
      tilpas: this.upcomingChange ? null : this.tilpas.value,
      sendLetter: this.sendLetter.value,
      rowVersion: this.serviceaftale.rowVersion,
    };

    this.loader.show();

    this.serviceaftaleService
      .updateServiceabonnement({
        caller: EGIS_SERVICE_CALLER,
        body: body,
      })
      .pipe(
        finalize(() => {
          this.loader.hide();
        }),
        takeUntil(this.unsubscribe$),
      )
      .subscribe(
        () => {
          stepper.next();
          this.stepperIndex = stepper.selectedIndex;
        },
        (error) => {
          this.errorHandler.HandleError(error);
        },
      );
  }

  getCurrentPrice() {
    this.priceService
      .getPriceForServiceAftale({
        caller: EGIS_SERVICE_CALLER,
        serviceaftaleId: this.serviceaftale.id,
      })
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (price: GetPriceServiceaftaleResponseDto) => {
          this.currentPrice = price;
        },
        (error: HttpErrorResponse) => {
          this.handlePriceError(error);
        },
      );
  }

  getNewPrice() {
    this.clearPrices();

    let tilvalgnrList = !this.selectedAbonnement?.isTilkaldsabonnement
      ? this.tilvalg.value.filter((x) => x != null && Number.isSafeInteger(x))
      : null;

    const newRequest: GetPriceRequest = {
      serviceaftaleId: this.serviceaftale.id,
      serviceabonnementId: this.selectedAbonnement.id,
      naesteEftersynDate: this.selectedAbonnement.isTilkaldsabonnement ? null : new Date(this.naesteEftersynDate.value).toOkDateString(),
      opkraevningDate: this.selectedAbonnement.isTilkaldsabonnement ? null : new Date(this.opkraevningDate.value).toOkDateString(),
      opkraevningsinterval: this.selectedAbonnement.isTilkaldsabonnement ? null : this.opkraevningsinterval.value,
      tilvalgnrList: tilvalgnrList,
      validFrom: this.validFrom.toOkDateString(),
    };

    const requestForKorrektion: GetPriceRequest = {
      serviceaftaleId: this.serviceaftale.id,
      serviceabonnementId: this.selectedAbonnement.id,
      naesteEftersynDate: this.selectedAbonnement.isTilkaldsabonnement ? null : new Date(this.naesteEftersynDate.value).toOkDateString(),
      opkraevningDate: this.selectedAbonnement.isTilkaldsabonnement ? null : new Date(this.opkraevningDate.value).toOkDateString(),
      opkraevningsinterval: this.selectedAbonnement.isTilkaldsabonnement ? null : this.serviceaftale.opkraevningsinterval,
      tilvalgnrList: tilvalgnrList,
      validFrom: this.validFrom.toOkDateString(),
    };

    this.priceError = null;

    forkJoin(
      this.priceService.getPriceForEditedServiceAftale({
        caller: EGIS_SERVICE_CALLER,
        body: newRequest,
      }),
      this.priceService.getPriceForEditedServiceAftale({
        caller: EGIS_SERVICE_CALLER,
        body: requestForKorrektion,
      }),
    )
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (results) => {
          this.newPrice = results[0];
          this.priceForKorrektion = results[1];
          this.opkraevningerTotalMedMoms = this.afregninger.length * results[1].opkraevning.amount.withMoms;
          this.opkraevningerTotalExclMoms = this.afregninger.length * results[1].opkraevning.amount.withoutMoms;
        },
        (error) => {
          this.handlePriceError(error);
        },
      );
  }

  clearPrices() {
    this.newPrice = null;
    this.priceForKorrektion = null;
    this.opkraevningerTotalMedMoms = null;
    this.opkraevningerTotalExclMoms = null;
  }

  handlePriceError(error: HttpErrorResponse) {
    if (error && error.status === 400) {
      const errorText = this.errorHandler.getValidationErrorText(error);
      this.priceError = errorText;
    }
  }

  getCurrentTilvalg(tilvalgGroup: TilvalgGroupDto): string {
    let value = null;
    this.serviceaftale.tilvalg.forEach((x) => {
      tilvalgGroup.tilvalg.forEach((y) => {
        if (x.tilvalgType == y.tilvalgType) {
          value = x.name;
        }
      });
    });
    return value;
  }

  getNewTilvalg(tilvalgGroup: TilvalgGroupDto) {
    const t = this.getCurrentTilvalg(tilvalgGroup);
    let value = null;
    this.tilvalg.value.forEach((x) => {
      tilvalgGroup.tilvalg.forEach((y) => {
        if (x == y.tilvalgnr) {
          value = y.name;
          return value;
        }
      });
    });
    return value !== t ? value : null;
  }

  getProductText(serviceAftale: ServiceaftaleDto) {
    return serviceAftale && serviceAftale.serviceAbonnement && serviceAftale.serviceAbonnement.product
      ? serviceAftale.serviceAbonnement.product.text.toLowerCase()
      : '';
  }

  goBack() {
    if (this.stepperIndex != 0) {
      this.stepper.previous();
    } else {
      this.goToSubscription();
    }
  }

  goToStep(stepNumber: number) {
    this.stepper.selectedIndex = stepNumber;
  }

  goToSubscription() {
    this.router.navigate(['/aftale', this.serviceaftale.okKundeAftaleNr], { fragment: 'Abonnement' });
  }

  onAbonnementChange(event: MatRadioChange) {
    const abonnement = this.abonnementer.find((x) => x.id == event.value);
    this.selectedAbonnement = abonnement;

    if (abonnement.eftersynInterval < this.serviceaftale.serviceAbonnement.eftersynInterval) {
      const currentServiceintervalStartDate = new Date(this.serviceaftale.currentServiceinterval.startDate);
      const newServiceintervalStartdate = new Date(
        currentServiceintervalStartDate.setMonth(currentServiceintervalStartDate.getMonth() + abonnement.eftersynInterval),
      );
      this.isTilpasningMulig = newServiceintervalStartdate < new Date() ? false : true;
    } else {
      this.isTilpasningMulig = !this.serviceaftale.serviceAbonnement.isTilkaldsabonnement ? true : false;
    }

    this.tilpas.setValue(this.isTilpasningMulig && !this.upcomingChange);

    let eftersynDate = this.serviceaftale.naesteEftersynDate ? this.serviceaftale.naesteEftersynDate : this.serviceaftale.baseEftersynDate;
    this.naesteEftersynDate.setValue(eftersynDate ? eftersynDate : new Date());
    this.eftersynInterval.setValue(abonnement.eftersynInterval);

    let defaultOpkraevningsDate = new Date();
    defaultOpkraevningsDate.setDate(new Date().getDate() + 45);
    let opkraevning = this.serviceaftale.opkraevningDate ? this.serviceaftale.opkraevningDate : this.serviceaftale.baseOpkraevningDate;
    this.opkraevningDate.setValue(opkraevning ? opkraevning : defaultOpkraevningsDate);

    this.nytServiceinterval = this.calculateNytServiceinterval(abonnement, this.validFrom);
    this.tilpassetServiceinterval = this.isTilpasningMulig
      ? this.calculateTilpassetServiceinterval(abonnement, this.currentServiceinterval)
      : null;

    if (this.upcomingChange) {
      this.selectedServiceinterval = this.nytServiceinterval;
    } else {
      if (this.tilpas.value) {
        this.selectedServiceinterval = this.tilpassetServiceinterval;
      } else {
        this.selectedServiceinterval = this.nytServiceinterval;
      }
    }

    this.minPaymentDate = this.getMinPaymentDate(this.serviceaftale);
    this.minCheckupDate = this.getMinCheckupDate();

    this.tilvalgService
      .getTilvalgGroupsForServiceabonnement({
        caller: EGIS_SERVICE_CALLER,
        serviceAbonnementId: event.value,
      })
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((tilvalgGroups: TilvalgGroupDto[]) => {
        this.selectedTilvalgGroups = tilvalgGroups;

        const control = <UntypedFormArray>this.tilvalgForm.controls['tilvalgGroups'];
        control.clear();

        this.selectedTilvalgGroups.forEach((x) => {
          control.push(new UntypedFormControl(null, [Validators.required]));
        });
        this.abonnementForm.updateValueAndValidity();

        this.getNewPrice();
      });
  }

  onPeriodeChange(event: MatRadioChange) {
    this.selectedServiceinterval = event.value ? this.tilpassetServiceinterval : this.nytServiceinterval;
    this.minPaymentDate = this.getMinPaymentDate(this.serviceaftale);
    this.minCheckupDate = this.getMinCheckupDate();
  }

  selectionChange(event: StepperSelectionEvent) {
    this.onStageResume = false;
    setTimeout(() => {
      this.onStageResume = true;
    }, 500);
  }

  calculateTilpassetServiceinterval(
    newServiceabonnement: ServiceAbonnementDto,
    currentServiceinterval: ServiceintervalDto,
  ): ServiceintervalDto {
    if (newServiceabonnement.isTilkaldsabonnement) return null;

    if (currentServiceinterval == null) return null;

    const startDate = currentServiceinterval?.serviceAbonnement.isTilkaldsabonnement
      ? new Date()
      : new Date(currentServiceinterval?.startDate);

    let endDate = new Date(startDate);
    endDate.setMonth(startDate.getMonth() + newServiceabonnement.eftersynInterval);
    endDate.setDate(endDate.getDate() - 1);

    var tilpassetServiceinterval: ServiceintervalDto = {
      startDate: startDate.toString(),
      endDate: endDate.toString(),
    };

    return tilpassetServiceinterval;
  }

  calculateNytServiceinterval(newServiceabonnement: ServiceAbonnementDto, validFrom: Date): ServiceintervalDto {
    if (newServiceabonnement.isTilkaldsabonnement) return null;

    var endDate = new Date(validFrom);
    endDate.setMonth(validFrom.getMonth() + newServiceabonnement.eftersynInterval);
    endDate.setDate(endDate.getDate() - 1);

    var nytServiceinterval: ServiceintervalDto = {
      startDate: validFrom.toString(),
      endDate: endDate.toString(),
    };

    return nytServiceinterval;
  }

  calculateNaesteeftersynsdate(serviceabonnement: ServiceAbonnementDto): Date {
    let eftersynDate = this.serviceaftale.naesteEftersynDate ? this.serviceaftale.naesteEftersynDate : this.serviceaftale.baseEftersynDate;
    return eftersynDate ? new Date(eftersynDate) : new Date();
  }

  getMinCheckupDate(): Date {
    return this.selectedServiceinterval?.startDate ? new Date(this.selectedServiceinterval?.startDate) : new Date();
  }

  getMinPaymentDate(serviceaftale: ServiceaftaleDto): Date {
    let minPaymentDate = new Date(this.selectedServiceinterval?.startDate);
    return minPaymentDate;
  }

  openOpkraevningsIntervalDialog() {
    var today = new Date();
    var isToday = today.toDateString() == this.validFrom.toDateString();
    let data: OpkraevningsDialogData = {
      changeEffect: isToday ? ChangeEffect.Instant : ChangeEffect.NextInterval,
      opkraevningsInterval: this.opkraevningsinterval.value ? this.opkraevningsinterval.value : this.selectedAbonnement.opkraevningInterval,
      opkraevningDate: new Date(this.opkraevningDate.value),
    };

    const dialogRef = this.dialog.open(OpkraevningsIntervalDialogComponent, {
      data: data,
    });

    dialogRef.afterClosed().subscribe((result: OpkraevningsDialogResponse) => {
      if (result) {
        this.opkraevningsinterval.setValue(result.opkraevningsInterval);
        this.getNewPrice();
      }
    });
  }

  clearOpkraevningsInterval() {
    this.opkraevningsinterval.setValue(null);
    this.getNewPrice();
  }

  reloadServiceAftale() {
    this.loader.show();
    this.serviceaftaleService
      .getServiceaftaleByNo({ caller: EGIS_SERVICE_CALLER, serviceaftaleNo: this.serviceaftaleNo })
      .pipe(
        takeUntil(this.unsubscribe$),
        finalize(() => {
          this.loader.hide();
        }),
      )
      .subscribe(
        (serviceaftale: ServiceaftaleDto) => {
          this.serviceaftale = serviceaftale;
        },
        (error) => {
          this.errorHandler.HandleError(error);
        },
      );
  }
}
